import type { Obj, StringUnion } from '../../../../typescript';
import { BASE_URLS, type BaseUrl } from '../../base-urls';
import { formatV2RequestParams, formatV2SearchParams, isResearchV2BaseUrl } from '../../research/v2';
import { stripEmpty } from '../../strip-empty';

/**
 * @summary Copied from https://github.com/lodash/lodash/blob/main/src/flow.ts
 *
 * ***This fn was copied from the `lodash` project because nextjs edge does not support all `lodash-es` functions
 * because they are optimized with some API's that aren't available in runtime.***
 *
 * @description Composes a function that returns the result of invoking the given functions
 * with the `this` binding of the created function, where each successive
 * invocation is supplied the return value of the previous;
 * `Array` return values will be spread as multiple args.
 * */
function flow(...funcs: Function[]) {
    const { length } = funcs;
    let i = length;
    while (i--) {
        if (typeof funcs[i] !== 'function') {
            throw new TypeError('Expected a function');
        }
    }
    // eslint-disable-next-line func-names
    return function (this: any, ...args: any[]) {
        let j = 0;
        let result = length ? funcs?.[j]?.apply(this, args) : args[0];
        while (++j < length) {
            /** this is a customization: ensures that an array is spread as multiple args */
            if (Array.isArray(result)) {
                result = result.flat();
                result = funcs?.[j]?.call(this, ...result);
            } else {
                result = funcs?.[j]?.call(this, result);
            }
        }
        return result;
    };
}

const getBaseUrl = (baseUrl: BaseUrl, override?: StringUnion<BaseUrl>) => {
    if (override) {
        return BASE_URLS[override as BaseUrl] || override;
    }
    return BASE_URLS[baseUrl];
};

const makeUnencodedParams = (params?: Obj<string | number>) => {
    if (!params) return '';

    const keys = Object.keys(params);

    if (!keys.length) return '';
    return keys.map(key => `${key}=${params[key]}`).join('&');
};

const makeSearchParams = (
    rawParams?: Obj<string | number>,
    unencodedParams?: Obj<string | number>,
    baseUrl?: BaseUrl,
    useResearchApiV2Mapper?: boolean,
) => {
    if (unencodedParams) {
        const search = makeUnencodedParams(stripEmpty(unencodedParams));
        return `?${search}`;
    }

    if (rawParams) {
        let params = { ...rawParams };

        if (isResearchV2BaseUrl(baseUrl)) {
            if (useResearchApiV2Mapper) {
                params = formatV2RequestParams(params);
            }
            return formatV2SearchParams(params);
        }

        const searchStr = new URLSearchParams(stripEmpty(params)).toString();
        if (searchStr) {
            return `?${searchStr}`;
        }
    }

    return '';
};

export { flow, getBaseUrl, makeSearchParams };
